import React, { useContext, useState, FC, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  GridLegacy as Grid,
  InputAdornment,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { FiAlertTriangle, FiCheckCircle } from "react-icons/fi";

import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../../components/notifications/progressDialog/progressDialog";
import LocationPickerDialog from "../../../../components/pickers/locationPicker/LocationPickerDialog";

import {
  api,
  AssetLocation,
  AssetLocationTypes,
  Booking,
  CommunicationChannel,
  IContactUpdate,
  IRecordOptInInput,
  IWorkflowRuleOptions,
  RequestStatus,
  SourceApp,
  UpsellPackage,
  UpsellPackagedProduct,
} from "../../../../logic/api";

import { SessionContext } from "../../../../utility/useSession";
import { validateTrackingIdFormat } from "@/utility/validation";
import { useDate } from "../../../../utility/useDate";
import UpsellSelector from "../../../../components/upsells/upsell_selector/UpsellSelector";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatDateTime, promisifyApi } from "../../../../utility/utils";
import SwitchField from "../../../../components/editable/EditableField/fields/SwitchField";
import PhoneInput, { isSupportedCountry, Country, isPossiblePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import classes from "./CheckInConfirmDialog.module.css";
import { ReleaseFlag, useReleaseManagement } from "../../../../utility/feature-management";

const COLLECTION_RECEIPT_UUID = "4069eb2f-3f33-40d1-bd4c-5b058e9c5158";

// #region Check In Dialog
interface ICheckInStateUpdate {
  booking?: Booking;
  location?: AssetLocation;
}

export interface ICheckInDialogProps {
  show: boolean;
  booking: Booking;
  onComplete: (booking: Booking | null) => void;
  onCancel: () => void;
}

export const CheckInDialog: FC<ICheckInDialogProps> = ({
  show,
  booking: parentBooking,
  onComplete,
  onCancel,
}) => {
  const session = useContext(SessionContext);
  const { localizeDate } = useDate();
  const { t } = useTranslation();

  const useUpsellListV2 = useReleaseManagement(ReleaseFlag.releaseImproveUpsellsPerformance);
  const isTrackingIdValidationReleased = useReleaseManagement(ReleaseFlag.releaseTrackingIdValidation);

  const getArrivalLocations = promisifyApi(api.hooks.assetLocation.getByParent());
  const requestCheckInBooking = promisifyApi(api.hooks.booking.checkIn());
  const listAvailableUpsells = useUpsellListV2 ? promisifyApi(api.hooks.upsell.listAvailableForBookingV2()) : promisifyApi(api.hooks.upsell.listAvailableForBooking());
  const recordOptIn = promisifyApi(api.hooks.communication.recordOptIn());
  const getEarlyCheckInRate = promisifyApi(api.hooks.booking.getEarlyCheckInRate());

  const [booking, setBooking] = useState<Booking>(parentBooking);
  const [location, setLocation] = useState<AssetLocation | undefined>(undefined);
  const [currentState, setCurrentState] = useState("");
  const [states, setStates] = useState<string[]>([]);
  const [upsellsAvailable, setUpsellsAvailable] = useState<boolean | null>(null);
  const [multipleArrivalLanesAvailable, setMultipleArrivalLanesAvailable] = useState<boolean | null>(false);
  const [initState, setInitState] = useState<[RequestStatus, unknown] | null>(null);
  const [earlyCheckInRate, setEarlyCheckInRate] = useState<number | null>(null);
  const [trackingValidationError, setTrackingValidationError] = useState<string>("");
  const [validatedTrackingId, setValidatedTrackingId] = useState<string>("");


  const preferences = session.current.site?.preferences;
  const booking_upsells_enabled = preferences?.booking_upsells_enabled == true;
  const collectionReceiptReleased = useReleaseManagement(ReleaseFlag.digitalCollectionReceipt);
  const collectionReceiptEnabled = collectionReceiptReleased &&
    (preferences?.collectionReceiptOptions?.channels ?? [])
      .filter(channel => channel.enabled)
      .length > 1;
  const earlyCheckInReleased = useReleaseManagement(ReleaseFlag.releaseEarlyCheckInDialog);
  const releaseAdditionalExternalReferences = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);
  const useTrackingId = Boolean(session.current.site?.preferences.use_tracking_id);

  const validateTrackingIdAPI = promisifyApi(api.hooks.booking.validateTrackingIdExternalReference());
  const setTrackingIdAPI = promisifyApi(api.hooks.booking.setTrackingId());

  const validateTrackingId = async (trackingRef: string) => {
    const validateTrackingIdResult = await validateTrackingIdAPI.send(session.current.context, { bookingId: booking.id, ref: trackingRef });
    return validateTrackingIdResult;
  }

  const onStepComplete = async (newState?: ICheckInStateUpdate) => {
    if (newState) {
      if (newState.booking && Boolean(newState.booking.leadContact)) {
        setBooking(newState.booking);
      }

      if (newState.location) {
        setLocation(newState.location);
      }
    }

    const currentIndex = states.indexOf(currentState);

    if (currentIndex < states.length - 1) {
      setCurrentState(states[currentIndex + 1]);
    } else {
      setCurrentState("checkingIn");
    }
  }

  const onConfirmStepComplete = async (trackingId?: string) => {
    if (trackingId && trackingId.trim().length > 0) {
      if (
        isTrackingIdValidationReleased &&
        !validateTrackingIdFormat(trackingId, session.current.site?.preferences.tracking_id_regex)
      ) {
        setTrackingValidationError(t("settings.invalidTrackingId"));
        return;
      }

      const validateTrackingIdResult = await validateTrackingId(trackingId.trim());

      if (validateTrackingIdResult?.success && validateTrackingIdResult.data) {
        setTrackingValidationError("");
        setValidatedTrackingId(validateTrackingIdResult.data.ref);
      } else {
        setTrackingValidationError(validateTrackingIdResult?.error || "Validation failed");
      }

      return;
    }

    onStepComplete();
  }

  const onConfirmValidatedTracking = async () => {
    if (validatedTrackingId) {
      const validateResult = await validateTrackingId(validatedTrackingId);
      if (validateResult?.success && validateResult.data) {
        await setTrackingIdAPI.send(session.current.context, { bookingId: booking.id, trackingId: validateResult.data });
      }
    }
    setValidatedTrackingId("");
    onStepComplete();
  }

  const onCancelValidatedTracking = () => {
    setValidatedTrackingId("");
    setTrackingValidationError("");
  }

  const checkInBooking = async () => {
    if (collectionReceiptReleased) {
      // Record opt-in if only one channel is enabled as we don't show the collection receipt dialog
      const optInChannels = (preferences?.collectionReceiptOptions?.channels ?? []).filter(channel => channel.enabled);
      if (optInChannels.length === 1) {
        const optInRecord: IRecordOptInInput = {
          bookingUuid: booking.uuid,
          contactId: booking.leadContact.id,
          communicationUuid: COLLECTION_RECEIPT_UUID,
          channels: [optInChannels[0].type],
          explicitOptIn: false,
          source: SourceApp.frontEnd,
        };
        await recordOptIn.send(session.current.context, optInRecord);
      }
    }

    const updatedBooking = await requestCheckInBooking.send(
      session.current.context,
      {
        id: booking.id,
        checkInDate: localizeDate(new Date()).toISO() ?? "",
        checkInSource: SourceApp.frontEnd,
        location: location,
      }
    );

    if (updatedBooking == null) {
      return false;
    }

    onComplete(updatedBooking);
    return true;
  };

  const cancelCheckIn = () => {
    setCurrentState(states[0]);
    setTrackingValidationError("");
    setValidatedTrackingId("");
    onCancel();
  };

  const buildStates = () => {
    const states = [];
    const hasEarlyCheckIn = earlyCheckInReleased && earlyCheckInRate !== null;
    const usingTrackingId = releaseAdditionalExternalReferences && useTrackingId;

    if (usingTrackingId) {
      if (hasEarlyCheckIn) {
        states.push("earlyCheckIn");
      }

      states.push("confirm");
    } else {
      if (hasEarlyCheckIn) {
        states.push("earlyCheckIn");
      } else if (!collectionReceiptEnabled) {
        states.push("confirm");
      }
    }


    if (upsellsAvailable && booking_upsells_enabled) {
      states.push("upsell");
    }

    if (multipleArrivalLanesAvailable || booking.selfPark) {
      states.push("arrivalLocation");
    }

    if (collectionReceiptEnabled) {
      states.push("collectionReceipt")
    }

    return states;
  };

  // Gather information to determine required states
  useEffect(() => {
    if (!show || !parentBooking) return;

    setBooking(parentBooking);
    setEarlyCheckInRate(null);
    setUpsellsAvailable(null);
    setMultipleArrivalLanesAvailable(null);

    const earlyCheckInPromise = earlyCheckInReleased
      ? getEarlyCheckInRate.send(session.current.context, { bookingId: parentBooking.id })
      : Promise.resolve(null);

    const upsellsPromise = session.current.site?.preferences.booking_upsells_enabled
      ? listAvailableUpsells.send(session.current.context, {
        bookingId: booking.id,
        paging: { offset: 0, limit: 1 }
      })
      : Promise.resolve(null);

    const arrivalLocationsPromise = getArrivalLocations.send(session.current.context, {
      types: [AssetLocationTypes.Lane],
      selectionTypes: [AssetLocationTypes.Lane],
      parentId: 0,
      arrivalsOnly: true
    });

    Promise.all([upsellsPromise, arrivalLocationsPromise, earlyCheckInPromise])
      .then(([upsells, locations, earlyCheckInRate]) => {
        setMultipleArrivalLanesAvailable(locations !== null ? Number(locations?.length) > 1 : false);
        setUpsellsAvailable(upsells !== null ? Number(upsells?.length) > 0 : false);
        setEarlyCheckInRate(earlyCheckInRate !== null ? earlyCheckInRate : null);
      });
  }, [show, parentBooking]);


  useEffect(() => {
    const status = [upsellsAvailable, multipleArrivalLanesAvailable].includes(null)
      ? RequestStatus.loading
      : RequestStatus.loaded;

    const requests = [listAvailableUpsells, getArrivalLocations];
    const requestError = requests.find(request => request.status === RequestStatus.error)?.err;

    setInitState([status, requestError]);
  }, [upsellsAvailable, multipleArrivalLanesAvailable]);

  // Once all information is gathered, build the states
  useEffect(() => {
    if (upsellsAvailable === null || multipleArrivalLanesAvailable === null) {
      return;
    }

    const states = buildStates();
    setStates(states);
    setCurrentState(states[0])
  }, [upsellsAvailable, multipleArrivalLanesAvailable, earlyCheckInRate]);

  // Once current state reaches checkingIn, check in the booking
  useEffect(() => {
    if (currentState === "checkingIn") {
      checkInBooking();
    }
  }, [currentState]);

  if (!show) {
    return <></>
  }

  return (<>
    {currentState === "confirm" && <CheckInConfirmDialog workflowRules={session.current.site?.preferences.workflowRules} onCancel={cancelCheckIn} onComplete={onConfirmStepComplete} isLastStep={states.length === 1} validationError={trackingValidationError} onClearError={() => setTrackingValidationError("")} useTrackingId={Boolean(session.current.site?.preferences.use_tracking_id)} validatedTrackingId={validatedTrackingId} onConfirmValidatedTracking={onConfirmValidatedTracking} onCancelValidatedTracking={onCancelValidatedTracking} />}
    {currentState === "earlyCheckIn" && <EarlyCheckInDialog booking={booking} onCancel={cancelCheckIn} onComplete={onStepComplete} dailyRate={earlyCheckInRate} isLastStep={states.length === 1} />}
    {currentState === "collectionReceipt" && <CollectionReceiptDialog booking={booking} onCancel={cancelCheckIn} onComplete={onStepComplete} />}
    {currentState === "upsell" && <CheckInUpsellDialog booking={booking} onCancel={cancelCheckIn} onComplete={onStepComplete} />}
    {currentState === "arrivalLocation" && <ArrivalLocationDialog booking={booking} onCancel={cancelCheckIn} onComplete={onStepComplete} />}

    {initState && (<ProgressDialog status={initState[0]} />)}
    {initState && (<ErrorDialog err={initState[1]} />)}

    <ProgressDialog status={requestCheckInBooking.status} />
    <ErrorDialog err={requestCheckInBooking.err} />
  </>);
};
// #endregion

// #region Early Check In Dialog
interface EarlyCheckInDialogProps {
  booking: Booking;
  dailyRate: number | null;
  isLastStep: boolean;
  onComplete: () => void;
  onCancel: () => void;
}

const EarlyCheckInDialog: FC<EarlyCheckInDialogProps> = ({
  booking,
  dailyRate,
  isLastStep,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);

  return (
    <Dialog open data-testid="checkin-state-early-checkin">
      <DialogTitle>{t("booking.labels.earlyCheckIn")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("confirmationDialog.checkIn.message")}
        </DialogContentText>
        <DialogContentText className={classes.error}>
          {t("confirmationDialog.earlyCheckIn.startDate", {
            startDate: formatDateTime(booking.bookingStartDate, session)
          })}
        </DialogContentText>
        <DialogContentText>
          {t("confirmationDialog.earlyCheckIn.feeWarning")}
        </DialogContentText>
        {dailyRate !== null && (
          <DialogContentText>
            {t("confirmationDialog.earlyCheckIn.dailyRate", {
              rate: formatCurrency(dailyRate, session)
            })}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>
          {t("general.cancel")}
        </Button>
        <Button onClick={onComplete}>
          <b>{isLastStep ? t("input.labels.checkIn") : t("general.continue")}</b>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// #endregion

// #region Confirm Dialog
interface ICheckInConfirmProps {
  workflowRules: IWorkflowRuleOptions | undefined,
  onComplete: (trackingId?: string) => void;
  onCancel: () => void;
  isLastStep: boolean;
  validationError?: string;
  onClearError?: () => void;
  useTrackingId: boolean;
  validatedTrackingId?: string;
  onConfirmValidatedTracking?: () => void;
  onCancelValidatedTracking?: () => void;
}

const CheckInConfirmDialog: FC<ICheckInConfirmProps> = ({
  workflowRules,
  onComplete,
  onCancel,
  isLastStep,
  validationError,
  onClearError,
  useTrackingId,
  validatedTrackingId,
  onConfirmValidatedTracking,
  onCancelValidatedTracking,
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const releaseAdditionalExternalReferences = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);
  const isTrackingIdValidationReleased = useReleaseManagement(ReleaseFlag.releaseTrackingIdValidation);
  const [trackingInput, setTrackingInput] = useState<string>("");
  const [regexValidationError, setRegexValidationError] = useState<boolean>(false);
  const continueButtonRef = useRef<HTMLButtonElement>(null);

  const trackingIdRegex = session.current.site?.preferences.tracking_id_regex;

  const shouldShowTrackingInput = releaseAdditionalExternalReferences && useTrackingId;
  const showConfirmation = shouldShowTrackingInput && validatedTrackingId && validatedTrackingId.length > 0;

  const isTrackingIdValid = (value: string) => {
    if (!shouldShowTrackingInput) return true;

    if (!value.trim()) {
      return !workflowRules?.requireTrackingIdOnCheckIn;
    }

    if (isTrackingIdValidationReleased) {
      return validateTrackingIdFormat(value, trackingIdRegex);
    }

    return true;
  };

  const isContinueEnabled = () => {
    if (!shouldShowTrackingInput) {
      return true;
    }

    if (trackingInput.trim().length > 0 && isTrackingIdValidationReleased && !validateTrackingIdFormat(trackingInput, trackingIdRegex)) {
      return false;
    }

    if (workflowRules?.requireTrackingIdOnCheckIn) {
      return (trackingInput.trim().length > 0 && !validationError);
    } else {
      return !validationError;
    }
  };

  const handleContinue = () => {
    if (!isTrackingIdValid(trackingInput)) {
      setRegexValidationError(true);
      return;
    }

    if (shouldShowTrackingInput && trackingInput.trim().length > 0) {
      onComplete(trackingInput.trim());
    } else {
      onComplete();
    }
  };

  const handleTrackingInputChange = (value: string) => {
    setTrackingInput(value);

    if (value && isTrackingIdValidationReleased && !validateTrackingIdFormat(value, trackingIdRegex)) {
      setRegexValidationError(true);
    } else {
      setRegexValidationError(false);
    }

    // clear any error when user starts typing
    if (validationError && onClearError) {
      onClearError();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      event.preventDefault();
      onConfirmValidatedTracking?.();
    }
  };

  if (showConfirmation) {
    return <Dialog open onKeyDown={handleKeyDown} data-testid="checkin-state-tracking-confirmation">
      <DialogTitle>{t("booking.labels.checkIn")}</DialogTitle>
      <DialogContent sx={{ minWidth: "300px" }}>
        {
          isTrackingIdValidationReleased ? (
            <Alert severity="success" title={t("booking.labels.trackingId")} data-testid="tracking-id-validation-success">
              {t("booking.labels.trackingId")}: <strong>{validatedTrackingId.toUpperCase()}</strong>
            </Alert>
          ) : (
            <DialogContentText>
              {t("booking.labels.trackingId")}: <strong>{validatedTrackingId}</strong>
            </DialogContentText>
          )
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelValidatedTracking} data-testid="tracking-confirmation-back-button">
          {t("general.back")}
        </Button>
        <Button ref={continueButtonRef} onClick={onConfirmValidatedTracking} data-testid="tracking-confirmation-continue-button">
          <b>{isLastStep ? t("input.labels.checkIn") : t("general.continue")}</b>
        </Button>
      </DialogActions>
    </Dialog>
  }

  return <Dialog open data-testid="checkin-state-confirm">
    <DialogTitle>{t("booking.labels.checkIn")}</DialogTitle>
    <DialogContent sx={{ minWidth: "300px" }}>
      <DialogContentText>
        {!shouldShowTrackingInput ? (
          t("confirmationDialog.checkIn.message")
        ) : (
          t("confirmationDialog.checkIn.trackingIdMessage")
        )}
      </DialogContentText>
    </DialogContent>
    {shouldShowTrackingInput && <TrackingIDInput value={trackingInput} onChange={handleTrackingInputChange} error={regexValidationError ? t("settings.invalidTrackingId") : validationError} onEnterPressed={handleContinue} showCharacterCounter={isTrackingIdValidationReleased} />}
    <DialogActions>
      <Button onClick={onCancel} data-testid="checkin-cancel-button">
        {t("general.cancel")}
      </Button>

      <Button disabled={!isContinueEnabled()} onClick={handleContinue} data-testid="checkin-continue-button">
        <b>{isLastStep ? t("input.labels.checkIn") : t("general.continue")}</b>
      </Button>
    </DialogActions>
  </Dialog>
}
// #endregion

// #region Collection Receipt Dialog
interface ICollectionReceiptProps {
  onCancel: () => void;
  onComplete: (newState?: ICheckInStateUpdate) => void;
  booking: Booking,
}

const CollectionReceiptDialog: FC<ICollectionReceiptProps> = ({
  onCancel,
  onComplete,
  booking,
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);

  const channels = session.current.site?.preferences.collectionReceiptOptions?.channels ?? [];
  const enabledChannels = channels.filter(channel => channel.enabled);
  const enabledChannelTypes = enabledChannels.map(channel => channel.type);
  const onByDefaultChannels = channels.filter(channel => channel.onByDefault).map(channel => channel.type);

  const [onChannels, setOnChannels] = useState<Set<CommunicationChannel>>(new Set<CommunicationChannel>(onByDefaultChannels));
  const [email, setEmail] = useState(booking.leadContact.email);
  const [emailValid, setEmailValid] = useState(true);
  const [phone, setPhone] = useState(booking.leadContact.phoneMobile);
  const [phoneValid, setPhoneValid] = useState(true);

  const updateContact = promisifyApi(api.hooks.contact.update());
  const recordOptIn = promisifyApi(api.hooks.communication.recordOptIn());

  const onSubmit = async () => {
    const contactUpdate: IContactUpdate = {
      ...booking.leadContact,
      accountNumber: booking.leadContact.accountNumber || undefined,
      bookingId: booking.id,
      dated: new Date().toISOString(),
      email: email,
      phoneMobile: phone.replaceAll(/\s/g, ""), // Replacement keeps consistency with the booking panel
    };
    const updatedContact = await updateContact.send(session.current.context, contactUpdate);
    if (updatedContact == null) {
      return;
    }

    const optIn: IRecordOptInInput = {
      bookingUuid: booking.uuid,
      contactId: booking.leadContact.id,
      communicationUuid: COLLECTION_RECEIPT_UUID,
      channels: [...onChannels.values()],
      explicitOptIn: true,
      source: SourceApp.frontEnd,
    };
    await recordOptIn.send(session.current.context, optIn);

    const updatedBooking = { ...booking, leadContact: updatedContact };
    onComplete({ booking: updatedBooking });
  }

  const enableChannel = (channel: CommunicationChannel) =>
    setOnChannels((state) => new Set(state.add(channel)));
  const disableChannel = (channel: CommunicationChannel) =>
    setOnChannels((state) => {
      state.delete(channel);
      return new Set(state);
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onPrintChange = (enabled: boolean, _fieldValue?: string) => {
    enabled ? enableChannel(CommunicationChannel.print) : disableChannel(CommunicationChannel.print);
  }

  const onEmailChange = (enabled: boolean, fieldValue?: string) => {
    enabled ? enableChannel(CommunicationChannel.email) : disableChannel(CommunicationChannel.email);
    const newValue = fieldValue ?? email;
    setEmail(newValue);
    setEmailValid(!enabled || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue));
  }

  const onPhoneChange = (enabled: boolean, fieldValue?: string) => {
    enabled ? enableChannel(CommunicationChannel.sms) : disableChannel(CommunicationChannel.sms);
    const newValue = fieldValue ?? phone;
    setPhone(newValue);
    setPhoneValid(!enabled || isPossiblePhoneNumber(newValue));
  }

  useEffect(() => {
    // Trigger validation of initial values.
    onEmailChange(onChannels.has(CommunicationChannel.email));
    onPhoneChange(onChannels.has(CommunicationChannel.sms));
  }, []);

  const formValid = onChannels.size > 0 && emailValid && phoneValid;

  return <>
    <Dialog open data-testid="checkin-state-collection-receipt">
      <DialogTitle>{t("confirmationDialog.collectionReceipt.title")}</DialogTitle>

      <DialogContent style={{ minWidth: "30vw" }}>
        <DialogContentText>
          {t("confirmationDialog.collectionReceipt.message")}
        </DialogContentText>

        {enabledChannelTypes.includes(CommunicationChannel.print) && (
          <CollectionReceiptDialogRow title="Print" fieldType="none" enabled={onChannels.has(CommunicationChannel.print)} onChange={onPrintChange} testId="collection-receipt-print" />
        )}

        {enabledChannelTypes.includes(CommunicationChannel.email) && (
          <CollectionReceiptDialogRow title="Email" fieldType="email" enabled={onChannels.has(CommunicationChannel.email)} fieldValue={email} onChange={onEmailChange} valid={emailValid} testId="collection-receipt-email" />
        )}

        {enabledChannelTypes.includes(CommunicationChannel.sms) && (
          <CollectionReceiptDialogRow title="SMS" fieldType="phone" enabled={onChannels.has(CommunicationChannel.sms)} fieldValue={phone} onChange={onPhoneChange} valid={phoneValid} testId="collection-receipt-sms" />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} data-testid="collection-receipt-cancel-button">
          {t("general.cancel")}
        </Button>

        <Button onClick={onSubmit} disabled={!formValid} data-testid="collection-receipt-checkin-button">
          <b>{t("input.labels.checkIn")}</b>
        </Button>
      </DialogActions>
    </Dialog>

    <ProgressDialog status={updateContact.status} />
    <ErrorDialog err={updateContact.err} />
    <ProgressDialog status={recordOptIn.status} />
    <ErrorDialog err={recordOptIn.err} />
  </>
}

interface CollectionReceiptDialogRowProps {
  title: string;
  fieldType: string;
  enabled: boolean;
  fieldValue?: string;
  valid?: boolean;
  onChange: (enabled: boolean, value?: string) => void;
  testId?: string;
}

const CollectionReceiptDialogRow: FC<CollectionReceiptDialogRowProps> = ({
  title,
  fieldValue,
  enabled: parentEnabled,
  fieldType,
  valid,
  onChange,
  testId,
}) => {
  const session = useContext(SessionContext);

  const [enabled, setEnabled] = useState(parentEnabled);

  const fieldRef = useRef(null);

  const defaultCountryCodePref = session.current.site?.preferences.default_country_code ?? "";
  const defaultCountryCode = isSupportedCountry(defaultCountryCodePref)
    ? defaultCountryCodePref
    : undefined;

  return <Grid container className={classes.collectionReceiptRow}>
    <Grid container item xs={10} className={classes.collectionReceiptField}>
      <Grid item xs={2} className={classes.collectionReceiptRowTitle}>
        <span>{title}</span>
      </Grid>

      <Grid item xs={8}>
        {fieldType === "email" && (
          <input
            value={fieldValue}
            style={{ width: "100%" }}
            type="email"
            ref={fieldRef}
            onChange={(e) => onChange(enabled, e.target.value)}
          />
        )}
        {fieldType === "phone" && (
          <PhoneInputWrapper
            defaultCountry={defaultCountryCode}
            value={fieldValue}
            onChange={(value) => onChange(enabled, (value ?? "").toString())}
          />
        )}
      </Grid>

      {fieldType !== "none" && (
        <Grid item xs={2} className={classes.collectionReceiptValidation}>
          {(valid)
            ? <FiCheckCircle color="#6bd076" />
            : <FiAlertTriangle color="red" />
          }
        </Grid>
      )}
    </Grid>

    <Grid item xs={2} className={classes.collectionReceiptSwitch}>
      <SwitchField
        onChange={(value) => { setEnabled(value); onChange(value, (fieldRef.current as unknown as HTMLInputElement)?.value); }}
        value={enabled}
        testId={testId ? `${testId}-switch` : undefined}
      />
    </Grid>
  </Grid>
}
// #endregion

// #region Upsell Dialog
interface ICheckInUpsellProps {
  onComplete: () => void;
  onCancel: () => void;
  booking: Booking;
}

const CheckInUpsellDialog: FC<ICheckInUpsellProps> = ({
  onComplete,
  onCancel,
  booking,
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);

  const applyUpsellPackage = promisifyApi(api.hooks.upsell.applyUpsellPackage());

  const applyCheckInUpsell = async (upsellPackage?: UpsellPackage, upsellPackageProduct?: UpsellPackagedProduct) => {
    if (upsellPackage?.id == null || upsellPackageProduct?.ref == null) {
      onComplete();
      return;
    }

    const result = await applyUpsellPackage.send(session.current.context, {
      bookingId: booking.id,
      upsellPackageId: upsellPackage.id,
      packagedProductref: upsellPackageProduct.ref,
      saleDate: new Date().toISOString(),
    });

    if (result) {
      onComplete();
    }
  };

  return <>
    <Dialog open data-testid="checkin-state-upsell">
      <DialogContent >
        <div style={{ padding: 10 }}>
          <UpsellSelector onSelect={applyCheckInUpsell} bookingId={booking.id} />
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} data-testid="upsell-cancel-button">
          {t("general.cancel")}
        </Button>

        <Button onClick={() => onComplete()} data-testid="upsell-no-thanks-button">
          {t("general.noThanks")}
        </Button>
      </DialogActions>
    </Dialog>

    <ProgressDialog status={applyUpsellPackage.status} />
    <ErrorDialog err={applyUpsellPackage.err} />
  </>;
}
// #endregion

// #region Arrival Location Dialog
interface IArrivalLocationProps {
  onComplete: (newState: ICheckInStateUpdate) => void;
  onCancel: () => void;
  booking: Booking;
}

const ArrivalLocationDialog: FC<IArrivalLocationProps> = ({
  onComplete,
  onCancel,
  booking,
}) => {
  const { t } = useTranslation();

  return <LocationPickerDialog
    title={t("booking.labels.checkInVehicleLocation")}
    show
    displayTypes={[AssetLocationTypes.Zone, AssetLocationTypes.Lane]}
    selectionTypes={[AssetLocationTypes.Lane]}
    arrivalsOnly={(booking != null && booking.selfPark) == true ? false : true}
    testId="checkin-state-arrival-location"
    onChange={(location) => {
      if (location == null) {
        onCancel();
        return;
      }

      onComplete({ location });
    }}
  />
}
// #endregion

// #region Phone Input Wrapper

interface PhoneInputWrapperProps {
  defaultCountry?: Country;
  value?: string;
  onChange: (value?: string) => void;
}

const PhoneInputWrapper: React.FC<PhoneInputWrapperProps> = ({ defaultCountry, value, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    let parsedValue = value;
    if (value && value.length > 2) {
      if (defaultCountry === "GB" && value.startsWith("07")) {
        parsedValue = `+44${value.slice(1)}`;
      } else if (defaultCountry === "AU" && value.startsWith("04")) {
        parsedValue = `+61${value.slice(1)}`;
      }
    }

    onChange(parsedValue);
  }, []);

  const handleValueChange = useCallback(
    (newValue: string | undefined) => {
      onChange(newValue);
    },
    []
  );

  return (
    <PhoneInput
      defaultCountry={defaultCountry}
      value={value}
      ref={ref}
      onChange={handleValueChange}
      international
    />
  );
};

interface TrackingIDInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  onEnterPressed?: () => void;
  showCharacterCounter?: boolean;
}

const TrackingIDInput: React.FC<TrackingIDInputProps> = ({ value, onChange, error, onEnterPressed, showCharacterCounter }) => {
  const { t } = useTranslation();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (onEnterPressed && !error && value.trim().length > 0) {
        onEnterPressed();
      }
    }
  };

  return (
    <>
      <Box sx={{ mx: 2, mb: 3, mt: 2, minWidth: "300px" }}>
        <TextField
          fullWidth
          autoFocus
          size="small"
          label={t("booking.labels.trackingId")}
          variant="outlined"
          value={value}
          error={!!error}
          helperText={error}
          slotProps={{
            htmlInput: {
              maxLength: showCharacterCounter ? 255 : 10,
              autoComplete: 'off',
            },
            input: !showCharacterCounter ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    {value.length}/10
                  </Typography>
                </InputAdornment>
              ),
            } : undefined
          }}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
        />
      </Box>
    </>
  )
}

// #endregion

